/** End-of-Day Report slice — the reference eodModel() (script lines 2922–3105) ported verbatim: report list, report
 *  detail, planned-vs-actual, bypass audit, override events. Data: `c.data.eod.eodSessions` (EOD_SESSIONS),
 *  `c.data.eod.eodBypassHistory` (EOD_BYPASS_HISTORY), `c.data.core.revDays` (REV_DAYS). */
import type { EodBypassSession, EodOverride, RevEntry } from '@pointfive/contracts';
import type { Ctx } from './ctx';
import type { ViewModel } from '@/screens/generated/view-model';

const W = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  M = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const W_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const M_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function buildEod(c: Ctx) {
  const S = c.S;
  const tone = (v: number, hi: number, mid: number) =>
    v >= hi ? 'var(--pos)' : v >= mid ? 'var(--amb-2)' : 'var(--neg)';
  const dayName = (off: number) => {
    const d = new Date(Date.UTC(2026, 6, 24) - off * 86400000);
    return {
      short: W[d.getUTCDay()] + ', ' + M[d.getUTCMonth()] + ' ' + d.getUTCDate() + ', ' + d.getUTCFullYear(),
      long:
        W_LONG[d.getUTCDay()] +
        ', ' +
        M_LONG[d.getUTCMonth()] +
        ' ' +
        d.getUTCDate() +
        ', ' +
        d.getUTCFullYear(),
      tiny: M[d.getUTCMonth()] + ' ' + d.getUTCDate(),
    };
  };

  const REV_DAYS = c.data.core.revDays;
  const reports = c.data.eod.eodSessions.map((s, i) => {
    const D = REV_DAYS[i];
    const ents: RevEntry[] = D ? D.entries : [];
    const pnl = ents.reduce((a, e) => a + e[3], 0);
    const wins = ents.filter((e) => e[3] > 0),
      losses = ents.filter((e) => e[3] < 0);
    const wr = ents.length ? (wins.length / ents.length) * 100 : 0;
    const best = ents.length ? ents.slice().sort((a, b) => b[3] - a[3])[0] : null;
    const worst = ents.length ? ents.slice().sort((a, b) => a[3] - b[3])[0] : null;
    const sym: Record<string, { trades: number; pnl: number }> = {};
    ents.forEach((e) => {
      const v = (sym[e[0]] = sym[e[0]] || { trades: 0, pnl: 0 });
      v.trades++;
      v.pnl += e[3];
    });
    const used: Record<string, { pnl: number; n: number }> = {};
    ents.forEach((e) => {
      const v = (used[e[4]] = used[e[4]] || { pnl: 0, n: 0 });
      v.pnl += e[3];
      v.n++;
    });
    // Null means the rules were never evaluated for that session — treated as the no-entries discount path rather
    // than as 0%, because "unmeasured" and "followed nothing" are different days and only one of them is a fact.
    const cleanRatio = (s.rulesFollowed ?? 0) / 100;
    const disc = ents.length
      ? Math.round(100 * (0.6 * cleanRatio + 0.4 * (wr / 100)))
      : Math.round(100 * cleanRatio * 0.75);
    return {
      i,
      dayIdx: i,
      off: s.off,
      meta: s,
      names: dayName(s.off),
      pnl,
      wins: wins.length,
      losses: losses.length,
      total: ents.length,
      wr,
      best,
      worst,
      disc,
      symbols: Object.entries(sym).map(([k, v]) => ({ symbol: k, ...v })),
      usedStrats: Object.entries(used).map(([k, v]) => ({ name: k, ...v })),
      untagged: 0,
    };
  });

  // An account with no end-of-day reports still has to render the screen that says so. This used to throw, and
  // because the whole view-model is built in one pass, a trader who had simply never closed a day saw the entire
  // product replaced by an error. The empty report is every field at its zero with a label that names the absence
  // — no invented session, no invented P&L.
  const R = reports[Math.min(S.eodSel, reports.length - 1)] ?? {
    i: 0,
    dayIdx: 0,
    off: 0,
    meta: {
      off: 0,
      acct: '—',
      started: false,
      rulesFollowed: 0,
      redFlags: 0,
      lockouts: 0,
      bypassed: false,
      auto: false,
      planned: [] as string[],
      tips: [] as string[],
      summary: 'No end-of-day report has been issued yet.',
    },
    names: dayName(0),
    pnl: 0,
    wins: 0,
    losses: 0,
    total: 0,
    wr: 0,
    best: null,
    worst: null,
    disc: 0,
    symbols: [] as { symbol: string; trades: number; pnl: number }[],
    usedStrats: [] as { name: string; pnl: number; n: number }[],
    untagged: 0,
  };
  const symPeak = Math.max(1, ...R.symbols.map((s) => Math.abs(s.pnl)));
  const plannedUsed = R.meta.planned.filter((p) => R.usedStrats.some((u) => u.name === p));
  const plannedNot = R.meta.planned.filter((p) => !R.usedStrats.some((u) => u.name === p));
  const usedNotPlanned = R.usedStrats.filter((u) => R.meta.planned.indexOf(u.name) < 0);
  const adherence =
    R.usedStrats.length || R.meta.planned.length
      ? Math.round((plannedUsed.length / Math.max(1, R.meta.planned.length + usedNotPlanned.length)) * 100)
      : null;

  // bypass audit
  const H = c.data.eod.eodBypassHistory;
  const byp = H.filter((s) => s.bypassed),
    prep = H.filter((s) => !s.bypassed);
  const avg = (a: EodBypassSession[], k: 'pnl' | 'wr') =>
    a.length ? a.reduce((x, s) => x + s[k], 0) / a.length : 0;
  const avgPnlB = avg(byp, 'pnl'),
    avgPnlP = avg(prep, 'pnl');
  const wrB = avg(byp, 'wr'),
    wrP = avg(prep, 'wr');
  const pnlDelta = avgPnlP - avgPnlB,
    wrDelta = wrP - wrB;
  const chart = H.slice().sort((a, b) => b.off - a.off);
  const cPeak = Math.max(1, ...chart.map((s) => Math.abs(s.pnl)));
  const overrides = H.filter((s): s is EodBypassSession & { override: EodOverride } => !!s.override);

  return {
    eodGenLabel: R.off === 0 ? "Regenerate Today's Report" : "Generate Today's Report",
    eodGen: () => c.flash('End-of-day report regenerated with the latest trade data', 'pos'),
    eodAcct: R.meta.acct,
    eodList: reports.map((r) => ({
      label: r.names.short,
      pnl: (r.pnl >= 0 ? '+' : '−') + '$' + Math.abs(r.pnl).toFixed(2),
      pnlFg: r.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
      trades: r.total === 1 ? '1 trade' : r.total + ' trades',
      acct: r.meta.acct,
      disc: String(r.disc),
      discBd: tone(r.disc, 70, 40),
      sel: r.i === S.eodSel,
      rowBg: r.i === S.eodSel ? 'var(--l6)' : 'transparent',
      markBd: r.i === S.eodSel ? 'var(--acc-fill)' : 'transparent',
      go: () => c.set({ eodSel: r.i, jDay: r.dayIdx }),
    })),
    // detail header
    eodDate: R.names.long,
    eodAuto: !!R.meta.auto,
    eodDisc: String(R.disc),
    eodDiscDash: (R.disc * 0.942).toFixed(1) + ' 100',
    eodDiscFg: tone(R.disc, 70, 40),
    eodDiscWord: R.disc >= 70 ? 'Strong' : R.disc >= 40 ? 'Moderate' : 'Needs Work',
    // kpis
    eodNet: (R.pnl >= 0 ? '+' : '−') + '$' + Math.abs(R.pnl).toFixed(2),
    eodNetFg: R.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
    eodWr: R.wr.toFixed(1) + '%',
    eodWrSub: R.wins + 'W / ' + R.losses + 'L of ' + R.total,
    eodBest: R.best ? '+$' + Math.abs(R.best[3]).toFixed(2) : 'N/A',
    eodBestSym: R.best ? R.best[0] : '',
    eodWorst: R.worst ? (R.worst[3] >= 0 ? '+$' : '−$') + Math.abs(R.worst[3]).toFixed(2) : 'N/A',
    eodWorstSym: R.worst ? R.worst[0] : '',
    // win/loss donut
    eodHasTrades: R.total > 0,
    eodNoTrades: R.total === 0,
    eodWinDash: R.total ? ((R.wins / R.total) * 100).toFixed(1) + ' 100' : '0 100',
    eodWinsN: String(R.wins),
    eodLossesN: String(R.losses),
    // symbol bars
    eodSymbols: R.symbols.map((s) => ({
      symbol: s.symbol,
      pnl: (s.pnl >= 0 ? '+$' : '−$') + Math.abs(s.pnl).toFixed(2),
      w: ((Math.abs(s.pnl) / symPeak) * 100).toFixed(1) + '%',
      bg: s.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
      fg: s.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
      trades: s.trades === 1 ? '1 trade' : s.trades + ' trades',
    })),
    eodHasSymbols: R.symbols.length > 0,
    eodNoSymbols: R.symbols.length === 0,
    // compliance
    eodCompliance: [
      {
        k: 'Session',
        v: R.meta.started ? 'Started' : 'Not Started',
        icon: R.meta.started ? 'ph-check-circle' : 'ph-x-circle',
        c: R.meta.started ? 'var(--pos)' : 'var(--neg)',
      },
      {
        k: 'Rules Followed',
        // An em dash, not 0%: a session whose rules were never evaluated has no adherence figure, and zero would
        // read as a verdict the day never earned.
        v: R.meta.rulesFollowed === null ? '—' : R.meta.rulesFollowed + '%',
        icon: 'ph-shield-check',
        c: (R.meta.rulesFollowed ?? 0) >= 70 ? 'var(--pos)' : 'var(--amb-2)',
      },
      {
        k: 'Red Flags',
        v: String(R.meta.redFlags),
        icon: 'ph-warning',
        c: R.meta.redFlags > 0 ? 'var(--neg)' : 'var(--pos)',
      },
      {
        k: 'Lockouts',
        v: String(R.meta.lockouts),
        icon: 'ph-lightning',
        c: R.meta.lockouts > 0 ? 'var(--neg)' : 'var(--pos)',
      },
    ],
    // ai
    eodSummaryParas: R.meta.summary.split('\n\n').map((t) => ({ t })),
    eodTips: R.meta.tips.map((t, i) => ({ n: String(i + 1), t })),
    eodHasTips: R.meta.tips.length > 0,
    // planned vs actual
    eodAdherence: adherence === null ? '—' : adherence + '%',
    eodAdherenceFg: adherence === null ? 'var(--muted2)' : tone(adherence, 80, 50),
    eodPlanned: R.meta.planned.map((p) => {
      const on = plannedUsed.indexOf(p) >= 0;
      return {
        name: p,
        icon: on ? 'ph-check-circle' : 'ph-x-circle',
        fg: on ? 'var(--pos)' : 'var(--muted2)',
        bg: on ? 'color-mix(in srgb, var(--pos) 10%, transparent)' : 'var(--l5)',
      };
    }),
    eodUsed: R.usedStrats.map((u) => {
      const off = R.meta.planned.indexOf(u.name) < 0;
      return {
        name: u.name,
        icon: off ? 'ph-warning' : 'ph-check-circle',
        iconFg: off ? 'var(--amb-2)' : 'var(--pos)',
        pnl: (u.pnl >= 0 ? '+' : '−') + Math.abs(u.pnl).toFixed(2),
        pnlFg: u.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
      };
    }),
    eodHasUsed: R.usedStrats.length > 0,
    eodNoUsed: R.usedStrats.length === 0,
    eodHasPlanned: R.meta.planned.length > 0,
    eodDeviations: plannedNot
      .map((p) => ({ label: 'Skipped: ' + p, icon: 'ph-x-circle', fg: 'var(--muted2)', bg: 'var(--l5)' }))
      .concat(
        usedNotPlanned.map((u) => ({
          label: 'Off-plan: ' + u.name,
          icon: 'ph-warning',
          fg: 'var(--amb-2)',
          bg: 'color-mix(in srgb, var(--amb) 10%, transparent)',
        })),
      ),
    eodAsPlanned: plannedNot.length === 0 && usedNotPlanned.length === 0,
    // bypass audit
    eodBypassCount: String(byp.length),
    eodSessionCount: String(H.length),
    eodAvgB: (avgPnlB >= 0 ? '+$' : '−$') + Math.abs(avgPnlB).toFixed(0),
    eodAvgBFg: avgPnlB >= 0 ? 'var(--pos)' : 'var(--neg)',
    eodAvgP: (avgPnlP >= 0 ? '+$' : '−$') + Math.abs(avgPnlP).toFixed(0),
    eodAvgPFg: avgPnlP >= 0 ? 'var(--pos)' : 'var(--neg)',
    eodDelta: (pnlDelta >= 0 ? '+$' : '−$') + Math.abs(pnlDelta).toFixed(0),
    eodDeltaFg: pnlDelta >= 0 ? 'var(--pos)' : 'var(--neg)',
    eodDeltaSub: (wrDelta >= 0 ? '+' : '−') + Math.abs(wrDelta).toFixed(1) + '% win rate',
    eodInsight:
      pnlDelta > 0
        ? 'Prepared sessions generate ' +
          (pnlDelta >= 0 ? '+$' : '−$') +
          Math.abs(pnlDelta).toFixed(0) +
          ' more on average and a ' +
          wrDelta.toFixed(1) +
          '% higher win rate. Preparation is measurably profitable.'
        : byp.length < 3
          ? 'Only ' +
            byp.length +
            ' bypassed session' +
            (byp.length === 1 ? '' : 's') +
            ' recorded — not enough data for a reliable comparison yet.'
          : 'Bypassed sessions currently outperform prepared ones by $' +
            Math.abs(pnlDelta).toFixed(0) +
            ' on average. Review whether your preparation routine is calibrated to your actual trading conditions.',
    eodInsightFg: pnlDelta > 0 ? 'var(--pos)' : 'var(--amb-2)',
    eodInsightIcon: pnlDelta > 0 ? 'ph-check-circle' : 'ph-warning',
    eodTicks: [
      0,
      Math.floor((chart.length - 1) / 3),
      Math.floor(((chart.length - 1) * 2) / 3),
      chart.length - 1,
    ]
      .filter((v, i, a) => a.indexOf(v) === i)
      .map((i) => chart[i])
      .filter((s): s is EodBypassSession => s !== undefined)
      .map((s) => ({ label: dayName(s.off).tiny })),
    eodChart: chart.map((s) => ({
      date: dayName(s.off).tiny,
      h: ((Math.abs(s.pnl) / cPeak) * 100).toFixed(1) + '%',
      pos: s.pnl >= 0,
      neg: s.pnl < 0,
      bg: s.bypassed ? 'var(--amb)' : s.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
    })),
    eodChartNote: 'Daily P&L — last ' + chart.length + ' sessions (amber bars = bypassed preparation)',
    eodBypassRows: byp.slice(0, 5).map((s) => ({
      date: dayName(s.off).tiny,
      at: s.at ? 'Bypassed at ' + s.at : '',
      trades: String(s.trades),
      wr: s.wr.toFixed(0) + '%',
      pnl: (s.pnl >= 0 ? '+$' : '−$') + Math.abs(s.pnl).toFixed(2),
      pnlFg: s.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
    })),
    eodHasBypass: byp.length > 0,
    // override events
    eodOverrides: overrides.map((s) => ({
      date: dayName(s.off).short,
      flags: s.override.flags.map((f) => ({ label: f.replace(/_/g, ' ') })),
      reason: s.override.reason === 'Trader acknowledged and proceeded' ? '' : '"' + s.override.reason + '"',
      hasReason: s.override.reason !== 'Trader acknowledged and proceeded',
      pnl: (s.pnl >= 0 ? '+$' : '−$') + Math.abs(s.pnl).toFixed(2),
      pnlFg: s.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
      trades: s.trades === 1 ? '1 trade' : s.trades + ' trades',
    })),
    eodHasOverrides: overrides.length > 0,
    eodOverrideCount: overrides.length + (overrides.length === 1 ? ' event' : ' events') + ' recorded',
  } satisfies Partial<ViewModel>;
}
